import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getTournaments, joinTournament, mapTournamentFromApi } from "./api/tournamentsApi";
import { useAuthUser } from "./auth/AuthUserContext";
import { sanitizeApiMessage } from "./auth/clientAuth";
import MatchVenueMap from "./components/MatchVenueMap";
import { SPORTS } from "./sportsOptions";

function formatEntryFee(value) {
  if (value === 0 || value === "0") return "Free";
  const n = typeof value === "number" ? value : Number.parseFloat(String(value));
  if (Number.isFinite(n)) {
    return `₹${n.toLocaleString("en-IN")}`;
  }
  return String(value);
}

function formatPrizeLabel(value) {
  if (value == null || String(value).trim() === "") return "—";
  const s = String(value).trim();
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = Number.parseFloat(s);
    return `₹${n.toLocaleString("en-IN")}`;
  }
  return s;
}

function TournamentList() {
  const navigate = useNavigate();
  const me = useAuthUser();
  const myUserId = typeof me?.userId === "number" ? me.userId : null;

  const [sportFilter, setSportFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [joinBusyId, setJoinBusyId] = useState(null);
  const [joinMessage, setJoinMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { response, data } = await getTournaments(sportFilter || undefined);
      if (!response.ok) {
        const raw =
          typeof data === "object" && data !== null && data.message
            ? data.message
            : `Could not load tournaments (${response.status})`;
        setError(sanitizeApiMessage(raw));
        setRows([]);
        return;
      }
      const list = Array.isArray(data) ? data : [];
      const mapped = list.map(mapTournamentFromApi).filter(Boolean);
      setRows(mapped);
    } catch {
      setError("Network error. Is the API running?");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [sportFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => rows, [rows]);

  useEffect(() => {
    if (!joinMessage) return undefined;
    const t = window.setTimeout(() => setJoinMessage(null), 4500);
    return () => window.clearTimeout(t);
  }, [joinMessage]);

  const handleJoin = async (tournament) => {
    setJoinMessage(null);
    if (myUserId == null) {
      navigate("/login", { state: { from: "/tournaments" } });
      return;
    }
    setJoinBusyId(tournament.id);
    setError("");
    try {
      const { response, data } = await joinTournament(tournament.id);
      if (response.status === 401) {
        navigate("/login", { state: { from: "/tournaments" } });
        return;
      }
      if (!response.ok) {
        const raw =
          typeof data === "object" && data !== null && data.message
            ? data.message
            : "Could not join this tournament.";
        setError(sanitizeApiMessage(String(raw)));
        return;
      }
      setJoinMessage("You joined this tournament.");
      await load();
    } catch {
      setError("Network error. Try again.");
    } finally {
      setJoinBusyId(null);
    }
  };

  return (
    <main className="page">
      <section className="panel">
        <div className="find-matches__head" style={{ alignItems: "flex-start", flexWrap: "wrap", gap: "0.75rem" }}>
          <h1 style={{ margin: 0 }}>Tournament listing</h1>
          <Link to="/create-tournament" className="ghost-btn small">
            Post a tournament
          </Link>
        </div>
        <div className="filters" style={{ marginTop: "1rem" }}>
          <select
            value={sportFilter}
            onChange={(e) => {
              setSportFilter(e.target.value);
            }}
          >
            <option value="">All Sports</option>
            {SPORTS.map((sport) => (
              <option key={sport} value={sport}>
                {sport}
              </option>
            ))}
          </select>
        </div>
        {loading ? <p className="muted">Loading tournaments…</p> : null}
        {error ? <p className="error-text">{error}</p> : null}
        {joinMessage ? <p className="success-text">{joinMessage}</p> : null}
      </section>
      <section className="card-grid">
        {!loading && !error && visible.length === 0 ? (
          <p className="muted">No tournaments yet. Be the first to post one.</p>
        ) : null}
        {visible.map((tournament) => (
          <article className="list-card tournament-card" key={tournament.id}>
            {tournament.latitude != null &&
            tournament.longitude != null &&
            !Number.isNaN(tournament.latitude) &&
            !Number.isNaN(tournament.longitude) ? (
              <MatchVenueMap
                latitude={tournament.latitude}
                longitude={tournament.longitude}
                title={tournament.name}
              />
            ) : null}
            <h3>{tournament.name}</h3>
            <p>Sport: {tournament.sportType}</p>
            <p>Date: {tournament.date ? new Date(tournament.date).toLocaleDateString() : "—"}</p>
            <p>Location: {tournament.location}</p>
            {tournament.createdByUsername ? (
              <p className="muted">Host: {tournament.createdByUsername}</p>
            ) : null}
            <p>Entry fee: {formatEntryFee(tournament.entryFee)}</p>
            <p>
              Teams: {tournament.registeredTeams ?? 0} / {tournament.teamSlots} filled
            </p>
            <p>
              Prizes: 1st {formatPrizeLabel(tournament.prizeFirst)} · 2nd {formatPrizeLabel(tournament.prizeSecond)}
              {tournament.prizeThird != null && String(tournament.prizeThird).trim() !== "" ? (
                <> · 3rd {formatPrizeLabel(tournament.prizeThird)}</>
              ) : null}
            </p>
            <button
              type="button"
              className="primary-btn"
              disabled={
                joinBusyId === tournament.id ||
                (tournament.createdByUserId != null && tournament.createdByUserId === myUserId) ||
                (tournament.registeredTeams ?? 0) >= tournament.teamSlots ||
                tournament.currentUserIsRegistered
              }
              onClick={() => void handleJoin(tournament)}
            >
              {joinBusyId === tournament.id
                ? "Joining…"
                : tournament.createdByUserId != null && tournament.createdByUserId === myUserId
                  ? "You are hosting"
                  : tournament.currentUserIsRegistered
                    ? "Joined"
                    : (tournament.registeredTeams ?? 0) >= tournament.teamSlots
                      ? "Tournament full"
                      : "Join tournament"}
            </button>
          </article>
        ))}
      </section>
    </main>
  );
}

export default TournamentList;
